import React from "react";
import ReactMarkdown from "react-markdown";
import { ChartBlock } from "./ChartBlock";
import { TableBlock } from "./TableBlock";

export const AssistantMessage = ({ blocks, isStreaming }) => {
  if (!blocks || blocks.length === 0) {
    if (isStreaming) {
      return (
        <div className="message assistant-message">
          <div className="message-content">
            <div className="streaming-indicator">
              <span className="streaming-dot"></span>
              <span className="streaming-dot"></span>
              <span className="streaming-dot"></span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  /* Filter only renderable content */
  const contentBlocks = blocks.filter((block) => {
    if (!block) return false;

    const rich = block.rich || block;
    const type = rich.type || block.simple?.type || block.type;
    const id = rich.id || block.id;

    const ignoreTypes = [
      "status_bar_update",
      "task_tracker_update",
      "chat_input_update",
    ];

    return type && !ignoreTypes.includes(type) && !id?.startsWith("doorsharp-");
  });

  if (contentBlocks.length === 0) return null;

  /* Sort blocks: text first, then charts, then others */
  const sortedBlocks = [...contentBlocks].sort((a, b) => {
    const getType = (block) => {
      const rich = block.rich || block;
      const simple = block.simple;
      return rich.type || simple?.type || block.type || "text";
    };
    
    const typeA = getType(a);
    const typeB = getType(b);
    
    const getPriority = (type) => {
      if (type === "text" || type === "markdown") return 1;
      if (type === "chart" || type === "plotly") return 2;
      if (type === "table" || type === "datatable" || type === "dataframe") return 3;
      return 4;
    };
    
    return getPriority(typeA) - getPriority(typeB);
  });

  /* Helpers */
  const extractContent = (block) => {
    const rich = block.rich || block;
    const simple = block.simple;

    // ✅ Simplified block (from useChatStream)
    if (block.content) return block.content;

    // Rich block - check data first
    if (rich?.data) {
      return (
        rich.data.content ||
        rich.data.text ||
        rich.data.value ||
        rich.data.markdown ||
        ""
      );
    }

    // Simple block
    if (simple) {
      return simple.text || simple.content || "";
    }

    // Rich block - direct properties
    if (rich) {
      return rich.content || rich.text || rich.value || "";
    }

    // Fallback
    return "";
  };

  const isMarkdown = (block) => {
    const rich = block.rich || block;
    const simple = block.simple;
    const type = rich.type || simple?.type || block.type;
    
    // Check type first
    if (type === "markdown") return true;
    
    // Check markdown flag in data
    if (rich?.data?.markdown === true) return true;
    
    // Check if block has markdown property
    if (block.markdown === true) return true;
    
    return false;
  };

  return (
    <div className="message assistant-message">
      <div className="message-content">
        {sortedBlocks.map((block, index) => {
          const rich = block.rich || block;
          const simple = block.simple;
          const type =
            rich.type || simple?.type || block.type || "text";

          /* ---------- TEXT / MARKDOWN ---------- */
          if (type === "text" || type === "markdown") {
            const content = extractContent(block);
            if (!content) return null;

            return (
              <div key={index} className="block text-block">
                <ReactMarkdown>{content}</ReactMarkdown>
              </div>
            );
          }

          /* ---------- CHART ---------- */
          if (type === "chart" || type === "plotly") {
            const chartData =
              rich?.data?.data ||
              rich?.data ||
              block.data;

            const chartLayout =
              rich?.data?.layout ||
              rich?.layout ||
              block.layout;

            if (!chartData) return null;

            return (
              <div key={index} className="block chart-block-wrapper">
                <ChartBlock data={chartData} layout={chartLayout} />
              </div>
            );
          }

          /* ---------- TABLE ---------- */
          if (
            type === "table" ||
            type === "datatable" ||
            type === "dataframe"
          ) {
            const data = rich?.data?.data || rich?.data || block.data;

            if (!Array.isArray(data) || data.length === 0) return null;

            const columns = Object.keys(data[0]);
            const rows = data.map((row) =>
              columns.map((col) => row[col])
            );

            return (
              <div key={index} className="block table-block-wrapper">
                <TableBlock columns={columns} rows={rows} />
              </div>
            );
          }

          /* ---------- FALLBACK ---------- */
          const fallback = extractContent(block);
          if (!fallback) return null;

          return (
            <div key={index} className="block text-block">
              <ReactMarkdown>{fallback}</ReactMarkdown>
            </div>
          );
        })}

        {isStreaming && (
          <span className="typing-cursor">▍</span>
        )}
      </div>
    </div>
  );
};
